// src/components/TeachingPlanCard/TeachingPlanCard.js
import React, { useMemo, useState } from "react";
import { Box, Paper, Button } from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { marked } from "marked";

import AiBorderWrapper from "../AIBorderWrapper";
import CollapsedPlanView from "./CollapsedPlanView";
import JourneyOverview2D from "./JourneyOverview2D";
import TeachingPlanEditor from "../plan/TeachingPlanEditor";
import parseTeachingPlanMarkdown from "./parseTeachingPlanMarkdown";

const collapseButtonSx = {
    textTransform: "none",
    borderRadius: 2,
    fontWeight: 600,
    borderColor: "divider",
    color: "text.primary",
};

/**
 * TeachingPlanCard
 * - Wraps everything in AiBorderWrapper + Paper
 * - Journey map always on top
 * - Collapsed: a short preview of the teaching plan underneath the map
 * - Expanded: the editable teaching plan (autosaved), which can be collapsed again
 */
export default function TeachingPlanCard({ finalMd, plan, onPlanChange, disabled }) {
    const [showFullPlan, setShowFullPlan] = useState(false);

    const { mluItems } = useMemo(
        () => parseTeachingPlanMarkdown(finalMd),
        [finalMd]
    );

    const renderMarkdown = (md) => ({
        __html: marked.parse(md || ""),
    });

    const canEdit = !!onPlanChange && (plan?.rows || []).length > 0;

    const collapse = () => {
        setShowFullPlan(false);
        document.getElementById("teaching-plan-card")?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <AiBorderWrapper aiActive animated={false} sx={{ mt: 1 }} id="teaching-plan-card">
            <Paper
                variant="outlined"
                sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "background.paper",
                    "& table": {
                        width: "100%",
                        borderCollapse: "collapse",
                        marginTop: 1,
                    },
                    "& th, & td": {
                        border: "1px solid",
                        borderColor: "divider",
                        padding: "6px 8px",
                        verticalAlign: "top",
                        fontSize: "0.875rem",
                    },
                    "& th": {
                        fontWeight: 600,
                        bgcolor: "grey.100",
                    },
                }}
            >
                {showFullPlan ? (
                    <>
                        {/* Top row with collapse button on the right */}
                        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                            <Button size="small" variant="outlined" endIcon={<ExpandLessIcon />} onClick={collapse} sx={collapseButtonSx}>
                                Collapse teaching plan
                            </Button>
                        </Box>

                        <JourneyOverview2D items={mluItems} />

                        <Box sx={{ mt: 3 }}>
                            {canEdit ? (
                                <TeachingPlanEditor plan={plan} onChange={onPlanChange} disabled={disabled} />
                            ) : (
                                <Box sx={{ overflowX: "auto", width: "100%" }}>
                                    <div dangerouslySetInnerHTML={renderMarkdown(finalMd)} />
                                </Box>
                            )}
                        </Box>

                        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                            <Button size="small" variant="outlined" endIcon={<ExpandLessIcon />} onClick={collapse} sx={collapseButtonSx}>
                                Collapse teaching plan
                            </Button>
                        </Box>
                    </>
                ) : (
                    <CollapsedPlanView
                        mluItems={mluItems}
                        rows={plan?.rows}
                        canEdit={canEdit}
                        onExpand={() => setShowFullPlan(true)}
                    />
                )}
            </Paper>
        </AiBorderWrapper>
    );
}
